import { ref, computed, watch } from "vue";
import parsePhoneNumberFromString from "libphonenumber-js";
import debounce from "lodash/debounce";
import { formatLocation } from "@/utils/const";
import { useContactData } from "@/composables/contacts/useContactData";
import { GET_PHONE_NUMBER_ROTATIONS } from "@/store/action-types";

export function usePhoneSelection(
  reactiveContact: any,
  phoneNumbers: any,
  deletePhoneMutation: any,
  store: any,
  account: any,
  toast: any
) {
  const selectedNumber = ref<string | null>(null);
  const selectedNumberObj = ref<any>(null);
  const selectedContactNumber = ref<any>(null);
  const showMicrophoneDropdown = ref(false);
  const showDialerDropdown = ref(false);
  const showIVRDropdown = ref(false);
  const searchTerm = ref("");
  const showSearchDropdown = ref(true);

  // Computed properties
  const allContactNumbers = computed(() => {
    const numbers: any[] = [];
    const contact = reactiveContact.value;

    if (!contact) {
      return numbers;
    }

    const getCallLogDataForNumber = (phoneNumber: string) => {
      if (!contact?.callLogs || !Array.isArray(contact.callLogs)) {
        return {
          callSid: null,
          callType: null,
          isValidLead: null,
          ivrNumbers: null,
          gateKeeperName: null,
        };
      }

      try {
        const callLog = contact.callLogs
          .filter((log: any) => log && log.to === phoneNumber)
          .sort(
            (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )[0];

        if (!callLog)
          return {
            callSid: null,
            callType: null,
            isValidLead: null,
            ivrNumbers: null,
            gateKeeperName: null,
          };

        return {
          callSid: callLog.callSid || null,
          callType: callLog.callType || null,
          isValidLead: callLog.isValidLead,
          ivrNumbers: callLog.ivrNumbers || null,
          gateKeeperName: callLog.gateKeeperName || null,
        };
      } catch (error) {
        console.error("Error getting call log data:", error);
        return {
          callSid: null,
          callType: null,
          isValidLead: null,
          ivrNumbers: null,
          gateKeeperName: null,
        };
      }
    };

    if (contact?.phones && Array.isArray(contact.phones) && contact.phones.length > 0) {
      contact.phones.forEach((p: any) => {
        if (!p || !p.number || typeof p.number !== "string" || p.number.trim().length === 0) return;
        const callLogData = getCallLogDataForNumber(p.number);
        const uiLabel =
          p.type === "DIRECT"
            ? "Direct"
            : p.type === "MOBILE"
              ? "Mobile"
              : p.type === "WORK"
                ? "Work"
                : p.type === "HOME"
                  ? "Home"
                  : "Other";
        const uiCallType =
          p.routeType === "DIRECT"
            ? "Direct"
            : p.routeType === "MOBILE"
              ? "Mobile"
              : p.routeType === "IVR"
                ? "IVR"
                : p.routeType === "GATEKEEPER"
                  ? "Gate Keeper"
                  : uiLabel;
        const ivrNumbers =
          p.ivrExtension && p.ivrExtension.length === 3
            ? p.ivrExtension.split("")
            : callLogData.ivrNumbers;
        numbers.push({
          number: p.number,
          type: uiLabel,
          typeEditable: true,
          callSid: callLogData.callSid,
          callType: uiCallType,
          isValidLead: typeof p.isValid === "boolean" ? p.isValid : callLogData.isValidLead,
          ivrNumbers,
          gateKeeperName: p.gatekeeperName || callLogData.gateKeeperName,
        });
      });
    }

    const c = contact || {};
    if (typeof c.mobileNumber === "string" && c.mobileNumber.trim()) {
      const data = getCallLogDataForNumber(c.mobileNumber);
      numbers.push({
        number: c.mobileNumber,
        type: "Mobile",
        callSid: data.callSid,
        callType: "Mobile",
        isValidLead: data.isValidLead,
        ivrNumbers: data.ivrNumbers,
        gateKeeperName: data.gateKeeperName,
      });
    }
    if (typeof c.directNumber === "string" && c.directNumber.trim()) {
      const data = getCallLogDataForNumber(c.directNumber);
      numbers.push({
        number: c.directNumber,
        type: "Direct",
        callSid: data.callSid,
        callType: "Direct",
        isValidLead: data.isValidLead,
        ivrNumbers: data.ivrNumbers,
        gateKeeperName: data.gateKeeperName,
      });
    }
    if (Array.isArray(c.additionalPhoneNumbers)) {
      c.additionalPhoneNumbers
        .filter((n: any) => typeof n === "string" && n.trim())
        .forEach((n: string) => {
          const data = getCallLogDataForNumber(n);
          numbers.push({
            number: n,
            type: "Other",
            callSid: data.callSid,
            callType: "Mobile",
            isValidLead: data.isValidLead,
            ivrNumbers: data.ivrNumbers,
            gateKeeperName: data.gateKeeperName,
          });
        });
    }

    if (Array.isArray(c.numberDialingInfo)) {
      c.numberDialingInfo
        .filter((n: any) => n && typeof n.phoneNumber === "string" && n.phoneNumber.trim())
        .forEach((n: any) => {
          const data = getCallLogDataForNumber(n.phoneNumber);
          const callType =
            n.routeType === "DIRECT"
              ? "Direct"
              : n.routeType === "MOBILE"
                ? "Mobile"
                : n.routeType === "IVR"
                  ? "IVR"
                  : n.routeType === "GATEKEEPER"
                    ? "Gate Keeper"
                    : "Mobile";
          numbers.push({
            number: n.phoneNumber,
            type: callType,
            callSid: data.callSid,
            callType,
            isValidLead: typeof n.isValid === "boolean" ? n.isValid : data.isValidLead,
            ivrNumbers: Array.isArray(n.ivrNumbers) ? n.ivrNumbers : data.ivrNumbers,
            gateKeeperName: n.gateKeeperName || data.gateKeeperName,
          });
        });
    }
    return numbers;
  });

  const validContactNumbers = computed(() =>
    allContactNumbers.value.filter(
      (phoneObj) =>
        phoneObj &&
        phoneObj.number &&
        typeof phoneObj.number === "string" &&
        phoneObj.number.trim().length > 0
    )
  );

  const contactPhoneNumbers = computed(() => {
    const { computeContactPhoneNumbers } = useContactData(store);
    return computeContactPhoneNumbers({ contact: reactiveContact.value });
  });

  const getRecipientPhoneNumber = computed(() => {
    if (reactiveContact.value?.phones && reactiveContact.value.phones.length > 0) {
      return reactiveContact.value.phones[0].number || "";
    }
    if (reactiveContact.value?.mobileNumber) {
      return reactiveContact.value.mobileNumber;
    }
    if (reactiveContact.value?.directNumber) {
      return reactiveContact.value.directNumber;
    }
    return "";
  });

  const hasValidPhoneNumber = computed(
    () => !!getRecipientPhoneNumber.value && getRecipientPhoneNumber.value.trim().length > 0
  );

  // Functions
  function onPhoneNumberChange(phoneNumber: any) {
    selectedNumberObj.value = phoneNumber;
    selectedNumber.value = phoneNumber?.number || phoneNumber;
  }

  function formatPhoneNumberWithCountry(option: any) {
    if (!option || !option.number) return option?.number || "";

    try {
      const phoneNumber = parsePhoneNumberFromString(option.number);
      if (phoneNumber && phoneNumber.isValid()) {
        const formattedNumber = phoneNumber.formatInternational();

        let locationText = "";
        if (option.city || option.region || option.countryCode) {
          locationText = formatLocation(option.city, option.region, option.countryCode);
        } else {
          const countryCode = phoneNumber.country;
          if (countryCode) {
            locationText = formatLocation(null, null, countryCode);
          }
        }

        const phoneNumberStyle =
          "font-weight: 500; color: #1f2937; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; font-size: 14px;";
        const locationStyle =
          "display: inline-flex; align-items: center; gap: 4px; color: #6b7280; font-size: 11px; font-weight: 400; white-space: nowrap; flex-shrink: 0; margin-left: 10px; padding: 2px 8px; background: #f3f4f6; border-radius: 12px;";
        const iconStyle = "font-size: 12px; color: #9ca3af; flex-shrink: 0;";
        const containerStyle =
          "display: flex; align-items: center; width: 100%; gap: 8px; flex-wrap: wrap;";

        return `
        <span style="${containerStyle}">
          <span style="${phoneNumberStyle}">${formattedNumber}</span>
          ${
            locationText
              ? `
            <span style="${locationStyle}">
              <svg style="${iconStyle}" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              ${locationText}
            </span>
          `
              : ""
          }
        </span>
      `.trim();
      }
    } catch (error) {
      console.warn("Error parsing phone number:", error);
    }

    return option.number;
  }

  function toggleDeleteContactPhoneNumber(phoneNumber: any) {
    selectedContactNumber.value = phoneNumber;
  }

  function deleteContactPhoneNumber() {
    if (!selectedContactNumber.value) return Promise.resolve();
    return deletePhoneMutation({
      id: reactiveContact.value?.id || (reactiveContact.value as any)?._bucket?.contactId,
      phoneNumber: selectedContactNumber.value.number,
    });
  }

  function getPhoneNumbers() {
    store
      .dispatch(GET_PHONE_NUMBER_ROTATIONS, {
        to: "",
        selectedNumber: selectedNumber.value,
        organizationId: account.value?.organizationId,
      })
      .then((res: any) => {
        if (res) {
          if (res.length > 0) {
            const currentIndex = res.findIndex((item: any) => item.number === selectedNumber.value);
            if (currentIndex !== -1 && currentIndex < res.length - 1) {
              selectedNumber.value = res[currentIndex + 1].number;
            }
          }
        }
      });
  }

  function selectMicrophone(deviceId: string) {
    const Twilio = (window as any).Twilio;
    if (Twilio?.setMicrophone) {
      Twilio.setMicrophone(deviceId);
      toast.info("Switched mic!");
      showMicrophoneDropdown.value = false;
    }
  }

  function handleClickOutside(event: MouseEvent) {
    const dropdown = (event.target as any)?.closest?.(".microphone-dropdown");
    if (!dropdown && showMicrophoneDropdown.value) {
      showMicrophoneDropdown.value = false;
    }
  }

  function handleDialerClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const dropdown = document.querySelector(".dialer-dropdown");
    const ivrDropdown = document.querySelector(".ivr-dropdown");
    if (dropdown && !dropdown.contains(target)) {
      showDialerDropdown.value = false;
      document.removeEventListener("mousedown", handleDialerClickOutside);
    }

    if (ivrDropdown && !ivrDropdown.contains(target)) {
      showIVRDropdown.value = false;
      document.removeEventListener("mousedown", handleDialerClickOutside);
    }
  }

  function toggleDialerDropdown() {
    showDialerDropdown.value = !showDialerDropdown.value;
    if (showDialerDropdown.value) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleDialerClickOutside);
      }, 0);
    } else {
      document.removeEventListener("mousedown", handleDialerClickOutside);
    }
  }

  function selectPhoneNumberToEdit(phoneNumberObj: any) {
    selectedContactNumber.value = phoneNumberObj;
  }

  function handlePhoneDataChange(phoneObj: any, updatedData: any) {
    phoneObj.callType = updatedData.callType;
    phoneObj.valid = updatedData.validity === "Valid";
    phoneObj.ivrNumber = updatedData.ivrNumber;
    phoneObj.gateKeeperName = updatedData.gateKeeperName;
  }

  function showPhoneNumberMenu(phoneObj: any) {
    selectPhoneNumberToEdit(phoneObj);
  }

  function getContacts() {
    const { getContacts } = useContactData(store);
    return getContacts(account.value, searchTerm.value);
  }

  function debouncedSearch() {
    if (/^[a-zA-Z]+$/.test(searchTerm.value)) {
      showSearchDropdown.value = true;
      getContacts();
    }
  }

  const debouncedSearchFunc = debounce(debouncedSearch, 100);

  function selectContact(contact: any) {
    if (!contact.mobileNumber && !contact.directNumber) {
      toast.info("This contact has no phone number");
      return;
    }
    searchTerm.value = contact.mobileNumber ?? contact.directNumber;
    showSearchDropdown.value = false;
  }

  function copyToClipboard(value: string) {
    const Clipboard = (window as any).Clipboard;
    if (Clipboard?.copy) {
      Clipboard.copy(value);
      toast.info("Copied to clipboard");
    }
  }

  // Watchers
  watch(
    phoneNumbers,
    (nums) => {
      if (nums?.length > 0 && !selectedNumber.value) {
        selectedNumber.value = nums[0]?.number;
        selectedNumberObj.value = nums[0];
      }
    },
    { immediate: true }
  );

  watch(showMicrophoneDropdown, (val) => {
    if (val) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
  });

  return {
    selectedNumber,
    selectedNumberObj,
    selectedContactNumber,
    showMicrophoneDropdown,
    showDialerDropdown,
    showIVRDropdown,
    searchTerm,
    showSearchDropdown,
    allContactNumbers,
    validContactNumbers,
    contactPhoneNumbers,
    getRecipientPhoneNumber,
    hasValidPhoneNumber,
    onPhoneNumberChange,
    formatPhoneNumberWithCountry,
    toggleDeleteContactPhoneNumber,
    deleteContactPhoneNumber,
    getPhoneNumbers,
    selectMicrophone,
    handleClickOutside,
    handleDialerClickOutside,
    toggleDialerDropdown,
    selectPhoneNumberToEdit,
    handlePhoneDataChange,
    showPhoneNumberMenu,
    getContacts,
    debouncedSearch,
    debouncedSearchFunc,
    selectContact,
    copyToClipboard,
  };
}
